import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  useCreateSmetaRequestMutation,
  useCreateWriteOffCheckMutation,
  useDeleteWriteOffCheckMutation,
  useGetWriteOffCheckQuery,
  useLazyExportWriteOffCheckXlsxQuery,
  useListSmetaContextOptionsQuery,
  useListWriteOffChecksQuery,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { extractFailedSources, statusLabel, statusTone } from './helpers';
import { CheckRow, Grid, Muted, ObjName, Row, Stack } from './styles';
import { ParseWarningsBanner } from '../ParseWarningsBanner';
import { CreateCheckCard } from './components/CreateCheckCard';
import { CheckDetailCard } from './components/CheckDetailCard';
import { RegistryPager, useRegistryPage, useSelectedRecordParam } from '../registryPaging';

export function WriteOffChecks() {
  const [objectName, setObjectName] = useState('');
  const [contractId, setContractId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [period, setPeriod] = useState('');
  const [ks2Files, setKs2Files] = useState<File[]>([]);
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
  const [selectedId, setSelectedId] = useSelectedRecordParam();
  const [poll, setPoll] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [requestNotice, setRequestNotice] = useState<string | null>(null);
  const { can_write } = useAgentPermission('smeta');
  const currentUserId = useAppSelector((s) => s.auth.user?.id);
  const { activeProjectId, activeContractId } = useActiveProjectContext();

  const page = useRegistryPage();
  const listQ = useListWriteOffChecksQuery(
    { limit: page.limit, offset: page.offset },
    { pollingInterval: 5000 },
  );
  const contextOptionsQ = useListSmetaContextOptionsQuery();
  const [createCheck, { isLoading: creating }] = useCreateWriteOffCheckMutation();
  const [deleteCheck] = useDeleteWriteOffCheckMutation();
  const [createRequest, { isLoading: sendingRequest }] = useCreateSmetaRequestMutation();
  const detailQ = useGetWriteOffCheckQuery(selectedId ?? '', {
    skip: !selectedId,
    pollingInterval: poll,
  });
  const [exportXlsx, { isFetching: exporting }] = useLazyExportWriteOffCheckXlsxQuery();
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId && activeProjectId) {
      setProjectId(activeProjectId);
      const found = contextOptionsQ.data?.projects.find((p) => p.id === activeProjectId);
      if (!objectName.trim() && found?.name) {
        setObjectName(found.name);
      }
    }
    if (!contractId && activeContractId) {
      setContractId(activeContractId);
    }
  }, [
    activeProjectId,
    activeContractId,
    contextOptionsQ.data?.projects,
    projectId,
    contractId,
    objectName,
  ]);

  useEffect(() => {
    if (!selectedId) {
      setPoll(0);
      return;
    }
    const st = detailQ.data?.check.status;
    setPoll(st === 'pending' || st === 'processing' ? 2500 : 0);
  }, [selectedId, detailQ.data?.check.status]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (ks2Files.length === 0 && invoiceFiles.length === 0) {
      setError('Приложите хотя бы один файл — акт КС-2 или накладную.');
      return;
    }
    const fd = new FormData();
    fd.append('object_name', objectName);
    if (contractId) fd.append('contract_id', contractId);
    if (projectId) fd.append('project_id', projectId);
    if (period.trim()) fd.append('period', period);
    ks2Files.forEach((f) => fd.append('ks2', f));
    invoiceFiles.forEach((f) => fd.append('invoice', f));
    try {
      const res = await createCheck(fd).unwrap();
      setSelectedId(res.check_id);
      setObjectName('');
      setPeriod('');
      setKs2Files([]);
      setInvoiceFiles([]);
    } catch (err) {
      setError('Не удалось запустить расчёт. Проверьте формат файлов и попробуйте снова.');
      // eslint-disable-next-line no-console
      console.error('createWriteOffCheck failed', err);
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteCheck(id).unwrap();
      if (selectedId === id) setSelectedId(null);
    } catch {
      // ignore — список обновится сам при следующем поллинге
    }
  }

  async function handleExport(id: string) {
    setExportError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `writeoff_m29_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('Не удалось выгрузить отчёт М-29.');
      // eslint-disable-next-line no-console
      console.error('exportWriteOffCheckXlsx failed', err);
    }
  }

  async function handleSendOverspendRequest(kind: 'legal_claim' | 'budget_limit_exceeded') {
    if (!detail || extractFailedSources(detail.check.summary).hasUnparsedKs2) return;
    setRequestNotice(null);
    setExportError(null);
    const overspends = detail.discrepancies.filter(
      (d) => d.kind === 'overspend' || d.kind === 'fact_no_norm',
    );
    const count = overspends.length || detail.discrepancies.length;
    try {
      await createRequest({
        kind,
        contract_id: detail.check.contract_id ?? undefined,
        project_id: detail.check.project_id ?? undefined,
        title:
          kind === 'legal_claim'
            ? `Претензия по перерасходу М-29: ${detail.check.object_name}`
            : `Перерасход материалов М-29: ${detail.check.object_name}`,
        summary: `Сметно-ценовой отдел выявил расхождения по М-29 на объекте «${detail.check.object_name}»${detail.check.period ? ` (${detail.check.period})` : ''}: позиций перерасхода/без основания — ${count}.`,
        details: {
          write_off_check_id: detail.check.id,
          object_name: detail.check.object_name,
          period: detail.check.period,
          discrepancy_count: detail.discrepancies.length,
          overspend_count: overspends.length,
        },
      }).unwrap();
      setRequestNotice(
        kind === 'legal_claim'
          ? 'Заявка на претензию по перерасходу отправлена в Претензионный модуль / Юристу.'
          : 'Сигнал о перерасходе отправлен в Экономиста.',
      );
    } catch {
      setExportError('Не удалось отправить межфункциональную заявку.');
    }
  }

  const checks = listQ.data?.items ?? [];
  const contextContracts = contextOptionsQ.data?.contracts ?? [];
  const contextProjects = contextOptionsQ.data?.projects ?? [];
  const detail = detailQ.data;
  const summaryWarnings = (() => {
    const s = detail?.check.summary;
    if (!s || typeof s !== 'object') return [];
    const w = (s as { warnings?: unknown }).warnings;
    return Array.isArray(w) ? w.filter((x): x is string => typeof x === 'string') : [];
  })();
  const isAuthor =
    !detail?.check.user_id ||
    !currentUserId ||
    detail.check.user_id === currentUserId;

  return (
    <Stack>
      <CreateCheckCard
        objectName={objectName}
        setObjectName={setObjectName}
        contractId={contractId}
        setContractId={setContractId}
        projectId={projectId}
        setProjectId={setProjectId}
        period={period}
        setPeriod={setPeriod}
        ks2Files={ks2Files}
        setKs2Files={setKs2Files}
        invoiceFiles={invoiceFiles}
        setInvoiceFiles={setInvoiceFiles}
        contextContracts={contextContracts}
        contextProjects={contextProjects}
        creating={creating}
        can_write={can_write}
        error={error}
        onSubmit={handleSubmit}
      />

      <Grid>
        <Stack>
          <RegistryPager
            label="Сессий"
            page={page}
            shown={checks.length}
            hasMore={listQ.data?.has_more}
            fetching={listQ.isFetching}
          />
          {checks.map((c) => {
            const { failedSources: rowFailed } = extractFailedSources(c.summary);
            return (
              <CheckRow key={c.id} $active={c.id === selectedId} onClick={() => setSelectedId(c.id)}>
                <Row>
                  <ObjName>{c.object_name}</ObjName>
                  <Badge $tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
                </Row>
                <Row>
                  <Muted>
                    {c.period ? `${c.period} · ` : ''}
                    {new Date(c.created_at).toLocaleString('ru-RU')}
                  </Muted>
                  {c.status === 'processing' && <Muted>{c.progress_percent}%</Muted>}
                  {rowFailed.length > 0 && (
                    <Badge $tone="warning">сбоев чтения: {rowFailed.length}</Badge>
                  )}
                </Row>
              </CheckRow>
            );
          })}
          {checks.length === 0 && (
            <Muted>Пока нет расчётов. Загрузите КС-2 и накладные выше.</Muted>
          )}
        </Stack>

        <div>
          {!selectedId && (
            <Muted>Выберите расчёт слева, чтобы увидеть баланс норма ↔ факт.</Muted>
          )}
          {selectedId && !detail && <Muted>Загрузка…</Muted>}
          {detail && (
            <Stack>
              <ParseWarningsBanner warnings={summaryWarnings} />
              <CheckDetailCard
              detail={detail}
              can_write={can_write}
              isAuthor={isAuthor}
              exporting={exporting}
              sendingRequest={sendingRequest}
              requestNotice={requestNotice}
              exportError={exportError}
              onExport={handleExport}
              onSendOverspendRequest={handleSendOverspendRequest}
              onDelete={handleDelete}
              />
            </Stack>
          )}
        </div>
      </Grid>
    </Stack>
  );
}

export default WriteOffChecks;
